<script lang="ts">
export default {
  name: 'ReactionForm',
  inheritAttrs: false,
  customOptions: {}
}
</script>

<script setup lang="ts">
import {ref, computed, watch, nextTick} from 'vue';
import {useGlobalStore} from '../stores/global';
import Address from '../components/Address.vue';
import LoginTest from '../components/LoginTest.vue'
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';

const props = defineProps({
  needs_premium: { type: Boolean, default: false },
  post_url: {type: String, required: true},
  verify_email_url: {type: String, required: true},
  login_url: {type: String, required: true},
  forgot_password_url: {type: String, default: ''},
  result_url: {type : String, default: ''},
  trans: {type: Object, required: true},
  // Ask the meta data when this reaction is NOT for a Business (like Franchise) or if the Business is for SALE
  ask_meta: { type: Boolean, default: false },
  select_options: {type: Object, required: true},
});

const remainingChars = computed(() => {
  return 1000 - (store.reaction.body ? store.reaction.body.length : 0);
});

const formRef = ref<HTMLElement | null>(null);
const bodyInputRef = ref<HTMLElement | null>(null);
const errorRef = ref<HTMLElement | null>(null);

const store = useGlobalStore();
let login_test_step = ref('verify_email');
if (store.user.id) {
  login_test_step.value = 'reaction';
}
let new_password = ref('');
let verify_password = ref('');

let saving = ref(false);
let error_msg = ref('');
let success_msg = ref('');

watch(login_test_step, async (_new) => {
  switch (_new) {
    case 'success':
    case 'register':
      login_test_step.value = 'reaction';
      break;
    case 'reaction':
      await nextTick(() => {
        bodyInputRef.value?.focus();
      });
      break;
  }
});

let validation_run = ref(false);
const validateForm = (): boolean => {
  validation_run.value = true;
  if (!store.reaction.body.length) {
    return false;
  }
  let u = store.user;
  if (props.ask_meta) {
    let m = u.meta;
    if (!m.areYou || !m.education || !m.experience || !m.capital) {
      return false;
    }
  }
  if (!u.firstName || !u.lastName) {
    return false;
  }
  let a = u.address;
  if (!a.postcode || !a.number || !a.street || !a.town) {
    return false;
  }
  if (!u.email || !u.phone || !u.phoneMobile) {
    return false;
  }
  if (!store.reaction.agree_terms) {
    return false;
  }

  validation_run.value = false;

  return true;
}

let saved = ref(false);
const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const saveReaction = async () => {
  error_msg.value = '';
  success_msg.value = '';
  saved.value = false;

  if (!validateForm()) {
    await nextTick(() => {
      let el = formRef.value?.querySelector('.p-invalid') as HTMLElement;
      if (el) {
        el.scrollIntoView({ behavior: isReducedMotion ? 'auto' : 'smooth' });
      }
    })
    return;
  }
  saving.value = true;

  const res = await store.postReaction(props.post_url, new_password.value);

  if (res.success) {
    if (res.hasOwnProperty('redirect') && res.redirect) {
      window.location.href = res.redirect;
      return;
    }

    if (props.result_url) {
      window.location.href = props.result_url;
      return;
    }

    success_msg.value = res.message;
    saved.value = true;
  } else {
    error_msg.value = res.message;
    errorRef.value?.scrollIntoView({ behavior: isReducedMotion ? 'auto' : 'smooth' });
  }

  saving.value = false;
};

const needsPayment = computed(() => {
  if (!props.needs_premium || store.user.has_premium) {
    return false;
  }

  return !store.user.id || store.user.isEntrepeneur;
});

const info = computed(() => {
  if (!props.trans.info?.length) {
    return '';
  }
  if (!props.needs_premium || store.user.has_premium) {
    return props.trans.info
  }
  if (!store.user.id) {
    return props.trans.premium_info1;
  }

  return store.user.isEntrepeneur ? props.trans.premium_info1 : props.trans.premium_info2;
});
const reset = () => {
  login_test_step.value = 'verify_email';
};
</script>

<template>
  <div class="flow" v-if="info.length > 0" v-html="info"></div>

  <form v-if="!saved" ref="formRef" class="form">
    <LoginTest v-if="login_test_step === 'verify_email' || login_test_step === 'login'"
               v-model:step="login_test_step" :verify_email_url="verify_email_url"
               :login_url="login_url" :forgot_password_url="forgot_password_url" :trans="trans"></LoginTest>

    <template v-else-if="login_test_step === 'reaction'">
<!--      <div v-if="trans.premium_info" class="row" v-html="trans.premium_info"></div>-->

      <div class="row">
        <div class="input-group">
          <label for="reaction-form-email">{{ trans.email }}</label>
          <input v-model="store.user.email" id="reaction-form-email" type="email" placeholder="" required readonly disabled />
          <a class="action reset" @click.prevent="reset" href="#">&times;</a>
        </div>
      </div>

      <hr style="margin:0;">

      <div class="row">
        <div>
          <label class="has-counter" for="reaction-form-reaction">{{ trans.notes }} <span
              class="count">{{ remainingChars }} {{ trans.chars }}</span></label>

          <p>{{ trans.notes_intro }}</p>
          <textarea ref="bodyInputRef" v-model="store.reaction.body" maxlength="1000" rows="6" id="reaction-form-reaction" type="text"
                    :class="{'p-invalid': validation_run && !store.reaction.body}" placeholder="" required></textarea>
          <p>{{ trans.notes_tip }}</p>
        </div>
      </div>

      <template v-if="ask_meta">
        <div class="row">
          <div>
            <label for="reaction-form-areyou">{{ trans.areyou }}</label>
            <Select id="reaction-form-areyou" v-model="store.user.meta.areYou" appendTo="self"
                    :invalid="validation_run && !store.user.meta.areYou"
                    :options="select_options.areYou" :placeholder="trans.make_choice" optionLabel="label" optionValue="id"
                    required></Select>
          </div>
          <div>
            <label for="reaction-form-education">{{ trans.education }}</label>
            <Select id="reaction-form-education" v-model="store.user.meta.education" appendTo="self"
                    :invalid="validation_run && !store.user.meta.education"
                    :options="select_options.education" :placeholder="trans.make_choice" optionLabel="label"
                    optionValue="id" required></Select>
          </div>
        </div>

        <div class="row">
          <div>
            <label for="reaction-form-experience">{{ trans.experience }}</label>
            <Select id="reaction-form-experience" v-model="store.user.meta.experience" appendTo="self"
                    :invalid="validation_run && !store.user.meta.experience"
                    :options="select_options.experience" :placeholder="trans.make_choice" optionLabel="label"
                    optionValue="id" required></Select>
          </div>

          <div>
            <label for="reaction-form-capital">{{ trans.capital }}</label>
            <Select id="reaction-form-capital" v-model="store.user.meta.capital" appendTo="self"
                    :invalid="validation_run && !store.user.meta.capital"
                    :options="select_options.capital" :placeholder="trans.make_choice" optionLabel="label"
                    optionValue="id" required></Select>
          </div>
        </div>
      </template>

      <hr style="margin:0;">

      <div class="flow">
        <h2>{{ trans.your_info }}</h2>

        <p>{{ trans.contact }}</p>
      </div>

      <div class="row">
        <div>
          <label for="reaction-form-form-of-address">{{ trans.formOfAddress }}</label>
          <div class="inputs-group inline">
            <label v-for="fa in select_options.formOfAddress" class="ui-radio">
              <input v-model="store.user.formOfAddress" id="reaction-form-form-of-address" name="formOfAddress" type="radio" :value="fa.id">
              <span>{{ fa.label }}</span>
            </label>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="size-md">
          <label for="reaction-form-first-name">{{ trans.first_name }}</label>
          <input v-model="store.user.firstName" :class="{'p-invalid': validation_run && !store.user.firstName}"
                 id="reaction-form-first-name" type="text" placeholder="" required/>
        </div>
        <div>
          <label for="reaction-form-last-name">{{ trans.last_name }}</label>
          <input v-model="store.user.lastName" :class="{'p-invalid': validation_run && !store.user.lastName}"
                 id="reaction-form-last-name" type="text" placeholder="" required/>
        </div>
      </div>

      <Address :trans="trans.address" :validation_run v-model:postcode="store.user.address.postcode"
               v-model:number="store.user.address.number"
               v-model:street="store.user.address.street"
               v-model:town="store.user.address.town" />

      <div class="row">
        <div>
          <label for="reaction-form-phone">{{ trans.phone }}</label>
          <input v-model="store.user.phone" :class="{'p-invalid': validation_run && !store.user.phone}" id="reaction-form-phone" type="tel"/>
        </div>

        <div>
          <label for="reaction-form-phone-mobile">{{ trans.mobile }}</label>
          <input v-model="store.user.phoneMobile" :class="{'p-invalid': validation_run && !store.user.phoneMobile}" id="reaction-form-phone-mobile" type="tel"/>
        </div>
      </div>

      <hr style="margin:0;">

      <template v-if="!store.user.id">
        <div class="flow">
          <h2>{{ trans.create_password }}</h2>

          <p>{{ trans.new_account }}</p>
        </div>
        <div class="row">
          <div>
            <label for="reaction-form-new-password">{{ trans.password }}</label>
            <input v-model="new_password" :class="{'p-invalid': validation_run && !new_password}"
                   id="reaction-form-new-password" type="password" minlength="6"
                   autocomplete="new-password" placeholder="" required>
          </div>

          <div>
            <label for="reaction-form-verify-password">{{ trans.password_repeat }}</label>
            <input v-model="verify_password" :class="{'p-invalid': validation_run && !(verify_password && (new_password === verify_password))}"
                   id="reaction-form-verify-password" type="password" minlength="6"
                   autocomplete="new-password" placeholder="" required>
          </div>
        </div>

        <hr style="margin:0;">
      </template>

      <label class="checkbox-wrap">
        <Checkbox v-model="store.reaction.register_newsletter" binary/>
        <span v-html="trans.register_newsletter"></span>
      </label>

      <label class="checkbox-wrap">
        <Checkbox v-model="store.reaction.agree_terms" :invalid="validation_run && !store.reaction.agree_terms" binary/>
        <span v-html="trans.agree_terms"></span>
      </label>

      <div ref="errorEl" v-if="error_msg" class="notice notice-error" v-html="error_msg"></div>

      <div v-if="false && needsPayment" class="notice notice-error">Needs premium</div>

      <div class="form-controls">
        <span></span>

        <button :disabled="saving" @click.prevent="saveReaction" class="btn-solid icon-rarr"
                :class="saving ? 'icon-spinner' : 'icon-mail'">
          {{ needsPayment ? trans.payment : trans.confirm }}
        </button>
      </div>
    </template>
  </form>

  <div v-if="success_msg" class="notice notice-success" v-html="success_msg"></div>
</template>
